"use client";

import React, { useCallback } from "react";
import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentRowScrollingWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 1;
  flex-grow: 1;
  padding-left: 60px;
  transition: padding ease 0.2s;
`;

const ContentRowScrollingContent = styled.div`
  transition: all ease 0.5s;
  display: flex;
`;

const ContentRowWrapper = styled.div`
  margin-bottom: 5rem;
  position: relative;
`;

const ContentRowTitle = styled.h2`
  color: white;
  margin: 0;
  margin-bottom: 22px;
  font-size: 27px;
  font-weight: 700;
  padding-right: 40px;
  padding-top: 40px;
`;

const CardContainer = styled.div`
  width: 256px;
  min-width: 256px;
  height: 370px;
  border-radius: 15px;
  position: relative;
  overflow: hidden;
  z-index: ${props => props.$focused ? 10 : 0};
  margin-left: 2rem;
  margin-bottom: 5%;
  cursor: pointer;
  backdrop-filter: blur(15px);
`;

const FocusBorderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  border-radius: 15px;
  pointer-events: none;
  z-index: 20;
`;

const ContentOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$focused ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  flex-direction: column;
  gap: 1rem;
  z-index: 15;
`;

const Title = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
`;

const Year = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
`;

const BannerContainer = styled.div`
  width: 100%;
  padding-top: 8px;
  border-radius: 24px;
  border: 3px solid #F5F5F5;
  background: black;
`;

const BannerBackground = styled.div`
  display: flex;
  height: 90%;
  min-height: 500px;
  align-items: flex-start;
  border-radius: 24px;
  background-image: url(${props => props.$bgImage});
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
`;

const BannerTitle = styled.h3`
  font-weight: 700;
  font-size: 52px;
  margin-top: 16px;
  margin-right: 16px;
  color: white;
`;

const BannerDescription = styled.h5`
  font-weight: 700;
  font-size: 32.5px;
  margin-bottom: 8px;
  color: #F0F0F0;
`;

const MovieItem = ({ item = {}, onFocus, isFirst }) => {
  const navigate = useNavigate();
  const { contentPhotos, title, year, slug, tags } = item || {};
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => {
      navigate(`/movie/${slug}`);
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        setFocus("MENU");
        return false;
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support
  const imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", {
    width: 200,
    quality: 90,
    format: 'jpeg'
  });

  return (
    <CardContainer
      ref={ref}
      $focused={focused}
      onClick={() => navigate(`/movie/${slug}`)}
      onMouseEnter={handleMouseEnter}
    >
      <OptimizedImage
        src={imageData.src}
        className="absolute inset-0 w-full h-full"
        style={{
          objectFit: 'cover',
          borderRadius: "15px",
        }}
      />
      {tags?.[0] && <ContentTag label={tags?.[0]} />}
      <FocusBorderOverlay $focused={focused} />
      <ContentOverlay $focused={focused}>
        <Title>{title}</Title>
        <Year>{year}</Year>
      </ContentOverlay>
    </CardContainer>
  );
};

const PentaBanners = ({
  bannerTitle,
  bannerDescription,
  bannerBackground,
  bannerItems,
  onFocus,
  rowTitle,
  id,
  index,
}) => {
  const { ref, focusKey } = useFocusable({
    onFocus,
    focusKey: "penta-banner" + id,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
  });

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  if (bannerItems.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className="max-w-full relative my-24 mx-10"
        ref={ref}
        tabIndex={index}
      >
        <BannerContainer>
          <BannerBackground $bgImage={bannerBackground}>
            <div className="flex flex-col justify-start h-full mr-8 text-[#F0F0F0]">
              <BannerTitle>{bannerTitle}</BannerTitle>
              <BannerDescription>{bannerDescription}</BannerDescription>
            </div>
          </BannerBackground>
        </BannerContainer>
        <div className="mt-[-15%] w-[90%] m-auto">
          <ContentRowWrapper>
            <ContentRowTitle>{rowTitle}</ContentRowTitle>
            <ContentRowScrollingWrapper ref={scrollingRef}>
              <ContentRowScrollingContent ref={innerRef}>
                {bannerItems?.map((item, idx) => {
                  return (
                    <MovieItem
                      item={item.content}
                      key={"penta_content_" + idx + id}
                      isFirst={idx === 0}
                    />
                  );
                })}
              </ContentRowScrollingContent>
            </ContentRowScrollingWrapper>
          </ContentRowWrapper>
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default PentaBanners;
